import React, { useState } from 'react';
import EditOrderLogin from '@ajat/design-system/EditOrderLogin';
import Button from '@ajat/design-system/Button';
import { ComponentPage, DemoCard } from '../../shared';

const Stage = ({ mobile = false, children }) => (
  <div
    className={`${mobile ? 'ds-edit-order-login-mobile-preview' : 'ds-edit-order-login-preview flex items-center justify-center'} bg-[var(--color-gray-025)] p-[var(--space-ui-large-s)] rounded-[var(--radius-card)] border border-[var(--color-gray-1)]`}
  >
    {children}
  </div>
);

export const meta = {
  component: EditOrderLogin,
  title: 'Edit Order Login',
  group: 'Overlays',
  importPath: '@ajat/design-system/EditOrderLogin',
  description:
    'Login popup for editing a placed order — email plus order number. Validates both before submitting; pass apiBrandSlug and it calls the backend itself, otherwise onSubmit hands the values to the parent. Reached from the account icon in the Header.',
  props: [
    ['isOpen', 'bool', 'false', 'Open state'],
    ['onClose', 'func', '—', 'Fires on X, Escape, overlay click and swipe'],
    ['onSubmit', 'func', '—', 'Fires with { email, orderNumber } when no apiBrandSlug is set'],
    ['onSuccess', 'func', '—', 'Fires with the order once the backend lookup succeeds'],
    ['onLogin', 'func', '—', 'Host-provided login: async (apiBrandSlug, { email, orderNumber }) → { token, order }; the design system has no backend client of its own'],
    ['apiBrandSlug', 'string', 'null', "Brand API slug, e.g. 'clsdk' — enables the built-in lookup"],
    ['notFoundMessage', 'string', '—', 'Message when the lookup finds no matching order'],
    ['banner', 'node', 'null', 'Banner image / node at the top'],
    ['title', 'string', "'Edit order'", 'Popup heading'],
    ['infoText', 'string', 'default copy', 'Intro copy'],
    ['emailLabel', 'string', '—', 'Email field label'],
    ['orderNumberLabel', 'string', '—', 'Order number field label'],
    ['submitLabel', 'string', '—', 'Submit button label'],
    ['emailErrorMessage', 'string', '—', 'Email validation message'],
    ['orderNumberErrorMessage', 'string', '—', 'Order number validation message'],
    ['validateEmail', 'func', '—', 'Custom email validator'],
    ['validateOrderNumber', 'func', '—', 'Custom order number validator'],
    ['initialEmail', 'string', "''", 'Prefilled email'],
    ['initialOrderNumber', 'string', "''", 'Prefilled order number'],
    ['preview', 'bool', 'false', 'Static mode — no overlay, no portal, no scroll lock'],
    ['lockScroll', 'bool', 'true', 'Lock body scroll while open'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'With the built-in lookup',
      code: `import EditOrderLogin from '@ajat/design-system/EditOrderLogin';

<EditOrderLogin
  isOpen={open}
  onClose={() => setOpen(false)}
  apiBrandSlug="clsdk"                 // component calls the API itself
  onSuccess={(order) => {
    setOpen(false);
    navigate(\`/clothing/cls-dk/edit-order/\${order.orderNumber}\`);
  }}
  notFoundMessage="Vi kunne ikke finde din ordre."
/>`,
    },
  ],
};

export default function EditOrderLoginPage() {
  const [open, setOpen] = useState(false);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live">
        <Button size="primary" variant="main" onClick={() => setOpen(true)}>Open Edit Order Login</Button>
        <EditOrderLogin isOpen={open} onClose={() => setOpen(false)} onSubmit={() => setOpen(false)} />
      </DemoCard>

      <DemoCard title="Desktop — popup" surface={false}>
        <Stage><EditOrderLogin isOpen preview onClose={() => {}} /></Stage>
      </DemoCard>

      <DemoCard title="Mobile — bottom sheet" surface={false}>
        <Stage mobile><EditOrderLogin isOpen preview onClose={() => {}} /></Stage>
      </DemoCard>
    </ComponentPage>
  );
}
